import { onBeforeMount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { LocalStorage, Notify } from 'quasar'
import { $t } from 'boot/common/i18n'
import DOMPurify from 'isomorphic-dompurify'

import { useAuthStore } from 'stores/receiver/account/auth/auth'
import { useUserStore } from 'stores/receiver/account/user/user'
import { useEventsStore } from 'stores/common/events'

import { useAuthHelpers } from 'composables/common/auth/useAuthHelpers'
import { useChangeLocale } from 'composables/common/i18n/useChangeLocale'

import type { RouteLocationRaw } from 'vue-router'

export const useAccessTokenCreation = () => {
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const { redirect } = useAuthHelpers()
  const { updateToUserLocale } = useChangeLocale()

  /**
   * Checks if an authentication code is present in the route query onBeforeMount.
   * If an authentication code is present, it calls the `createAccessToken` function.
   *
   * @param redirectUrl - Optional redirect URL after creating the access token.
   */
  const accessTokenOnBeforeMount = (redirectUrl?: string) =>
    onBeforeMount(async () => {
      if (route.query.authentication_code) {
        await createAccessToken(
          route.query.authentication_code as string,
          redirectUrl
        )
        await updateToUserLocale()
      }
    })

  /**
   * Creates an access token using the provided authentication code.
   *
   * @param authentication_code - The authentication code to use for creating the access token.
   * @param redirectUrl - Optional redirect URL after successful token creation.
   * @returns {Promise<void>} - A promise that resolves when the access token is created.
   */
  const createAccessToken = async (
    authentication_code: string,
    redirectUrl?: RouteLocationRaw
  ) => {
    // Fetch access token
    const res = await authStore.createTokens(authentication_code)
    if (res.success) {
      // No 2FA
      if (res.status === 200) {
        // Fetch the user info
        const success = await userStore.fetchUserInfo()
        if (success) {
          await updateToUserLocale()
          // Notify user on signup success
          if (route.query.signup) {
            Notify.create({
              message: DOMPurify.sanitize(
                `<p>${$t('account.create.success.title')}</p><p>${$t(
                  'account.create.success.subtitle'
                )}</p>`
              ),
              color: 'positive',
              html: true,
            })
          }
          // Notify user on successful password recovery
          if (route.query.pass) {
            Notify.create({
              message: DOMPurify.sanitize(
                `<p>${$t('account.recovery.success.title')}</p><p>${$t(
                  'account.recovery.success.subtitle',
                  {
                    email: route.query.pass,
                  }
                )}</p>`
              ),
              color: 'positive',
              html: true,
            })
          }
          // Clean queries
          if (
            route.query.authentication_code ||
            route.query.signup ||
            route.query.pass
          ) {
            // Remove the authentication_code when used
            const query = { ...route.query }
            delete query.authentication_code
            delete query.signup
            delete query.pass
            await router.replace({ path: route.path, query })
          }

          // Connect to event stream
          if (userStore.isLoggedIn && userStore.user) {
            const { subscribeEvents } = useEventsStore()
            subscribeEvents([userStore.user.channel_name])
          }

          const redirectStorage = LocalStorage.getItem('redirect')

          // Redirect to profile questions if user didn't answer them
          if (
            !userStore.user?.profile.job ||
            !userStore.user?.profile.firstname ||
            !userStore.user?.profile.lastname
          ) {
            if (redirectUrl) {
              LocalStorage.setItem('redirect', redirectUrl)
            }
            await router.push('/sign-up/profile')
          } else if (
            // Redirect to the invitation saved link
            redirectStorage &&
            (redirectStorage as string).includes('invitation')
          ) {
            redirect('/')
          } else if (
            process.env.FREE_PLAN !== '1' &&
            !userStore.subscription?.subscription_id
          ) {
            await router.push('/pricing/plans')
          }
          // redirect
          else if (redirectUrl) {
            redirect(redirectUrl)
          }
        }

        // 2FA
      } else if (res.status === 202 && authStore.challenges) {
        if (redirectUrl) {
          LocalStorage.setItem('redirect', redirectUrl)
        }
        const actives = authStore.activeChallenges

        if (actives.length > 1) {
          await router.push('/login/two-factor')
        } else if (actives.length === 1) {
          let type = ''
          switch (actives[0]) {
            case 'sms_challenge_active':
              type = 'sms'
              break
            case 'email_challenge_active':
              type = 'email'
              break
            case 'totp_challenge_active':
              type = 'totp'
              break
            case 'backup_codes_challenge_active':
              type = 'backup-codes'
              break
          }
          await router.push(`/login/two-factor/${type}`)
        }
      }
    }
  }

  return { createAccessToken, accessTokenOnBeforeMount }
}
